/* eslint-disable no-trailing-spaces */
import React from "react";

// components
import Link from "@/components/Link";

export const BOOKING_TERMS_DEFINITIONS = {
  title: "Definitions",
  id: "definition-aus",
  description: "The following definitions apply to your Booking:",
  options: [
    {
      title: "Access Date",
      description: "For each Booking, the start date of the hire period."
    },
    {
      title: "Additional Documentation",
      description: "Any documentation which the Location Provider requires you to complete or provide in connection with your intended use of the Space including without limitation completing health and safety forms, access permits or vehicle access permits, providing proof of any consents or licences or providing copies of any visuals, layouts or site plans."
    },
    {
      title: "Booking",
      description: "A booking made by you on the Platform (whether or not confirmed) to hire a Space for a specified period."
    },
    {
      title: "Booking Agent",
      description: "Location Live (Australia) Pty Ltd, a company incorporated and registered in New South Wales, Australia with company number ACN 622 143 300 whose registered office is at Suite 126 117 Old Pittwater Road, Brookvale NSW 2100, Australia and whose business premises are at Level 6/201 Kent St, Sydney NSW 2000, Australia."
    },
    { title: "Booking Confirmation", description: "For each Booking, a confirmation issued to you by the Platform to confirm the Booking." },
    { title: "Booking Fee", description: "The Cost of Space and any additional costs or charges specified in the Booking Form and confirmed in the Booking Confirmation." },
    { title: "Booking Form", description: "A form submitted by you on the Platform to make a Booking." },
    { title: "Cost of Space", description: "The cost to book a Space as specified in the Booking Form and confirmed in the Booking Confirmation." },
    { title: "Insurance Documents", description: "Policy certificates for any insurance policies you are required to effect and maintain in accordance with these Booking Terms. " },
    { title: "Licence", description: "A licence issued by the Location Provider permitting you to access, occupy and use a Space for the hire period." },
    { title: "Location", description: "The main site where a Space is located." },
    {
      title: "Location Live",
      description: "Location Live Limited, a company incorporated and registered in England and Wales with company number 08307715 whose registered office is at The Courtyard, 37 Sheen Road, Richmond, TW9 1AJ, United Kingdom."
    },
    { title: "Location Provider", description: "The site owner or site operator of the Location or a third party authorised to represent the site owner or site operator." },
    {
      title: "Platform",
      description: (<>
        <b>lo:live</b>, the online platform operated by Location Live for businesses and other organisations
        to search and book locations to conduct promotional and other activities. The platform is available
        at <Link className="locl-legal-page__description_link" rel="noreferrer" target="_blank" href="/lolive"><b>locationlive.com/lolive</b></Link>. For the purposes of these Booking Terms, actions stated to be performed
        by the Platform are performed by Location Live.
      </>),
    },
    { title: "Required Approvals", description: "Any matters requiring the Location Provider’s approval as notified to you prior to the Access Date." },
    { title: "Risk Documents", description: "Risk Assessment and Safe Working Method Statement (SWMS)." },
    { title: "Security Deposit", description: "A deposit held by the Location Provider to pay the costs of repairing any damage arising during the hire period, any overrun fees or any other amounts due to the Location Provider in connection with the Booking." },
    { title: "Space", description: "A designated area within a Location available for hire." },
    { title: "Specifics of Hire", description: "Additional terms applicable to your use of a Space as confirmed in the Booking Confirmation." }, 
    { title: "You", description: "The business or other organisation making the Booking on the Platform." },
  ].filter(Boolean),
};

export const BOOKING_TERMS_MAKING_BOOKING = {
  title: "Making a Booking",
  id: "booking-making-aus",
  options: [
    { description: "You make a Booking by submitting a Booking Form on the Platform." },
    { description: "When you make a Booking, you do so as principal. If you are making a Booking for one of your clients, you remain liable as principal for the Booking and the Licence connected to your Booking." },
    { description: "The Booking Agent is entitled at all times to rely on you having full authority on any and all matters relating to your Booking and the use of the Space." },
  ],
};

export const BOOKING_TERMS_AUTHORISATION = {
  id: "authorisation-aus",
  title: "Authorisation to enter into a Licence",
  options: [{ description: "When you submit a Booking Form, you authorise the Booking Agent to enter into a Licence on your behalf." }],
};

export const BOOKING_TERMS_ACCESS_CONDITIONS = {
  id: "access-conditions-aus",
  title: "Access conditions",
  options: [
    {
      description: (<>
        <p>
          You will not be permitted to access the Space on the Access Date
          (or at any other time during the hire period)
          unless you have satisfied the following the conditions:
        </p>
        <br/>
        <ol className="marker-bracket-type">
          <li>You must pay 100% of the Booking Fee to the Booking Agent.</li>
          <li>You must pay the Security Deposit (if any) to the Booking Agent.</li>
          <li>You must provide a copy of your Insurance Documents to the Location Provider.</li>
          <li>You must provide a copy of your Risk Documents to the Location Provider.</li>
          <li>
            You must complete or provide the Additional Documentation (if any) in a form satisfactory 
            to the Location Provider.
          </li>
          <li>You must obtain the Required Approvals from the Location Provider.</li>
        </ol>
      </>)
    }
  ]
};

export const BOOKING_TERMS_BOOKING_FEE = {
  id: "booking-fee-aus",
  title: "Booking fee",
  options: [
    { description: "By submitting a Booking Form, you become liable to pay the Booking Fee in accordance with these Booking Terms." },
    { description: "Unless otherwise stated in the Booking Confirmation, you must pay 100% of the Booking Fee 45 days prior to the Access Date. If you make a Booking with less than 45 days to go before the Access Date, you must pay 100% of the Booking Fee immediately." },
    { description: "The Booking Fee includes the Cost of Space and any additional costs or charges specified in the Booking Form and confirmed in the Booking Confirmation. However, the Booking Fee may not include all additional costs or charges arising in connection with your use of the Space (e.g. for your consumption of utilities). Any additional costs or charges which are not included in the Booking Fee will be charged separately to the Booking Fee and may be charged by the Booking Agent, the Location Provider or a third party supplier." },
  ]
};

export const BOOKING_TERMS_SECURITY_DEPOSIT = {
  id: "security-deposit-aus",
  title: "Security deposit",
  options: [
    { description: "This section only applies to your Booking if the Location Provider requires payment of a Security Deposit." },
    { description: "By submitting a Booking Form, you become liable to pay the Security Deposit in accordance with these Booking Terms." },
    { description: "Unless otherwise stated in the Booking Confirmation, you must pay the Security Deposit 10 days prior to the Access Date. If you make a Booking with less than 10 days to go before the Access Date, you must pay the Security Deposit immediately." },
    { description: "The Booking Agent shall return the Security Deposit to you as soon as possible after the end of the hire period less any deductions made by the Location Provider." },
  ]
};

export const BOOKING_TERMS_INSURANCE = {
  id: "insurance-aus",
  title: "Insurance",
  options: [
    { description: "You shall effect and maintain at your own cost reasonable and prudent policies of insurance with one or more reputable insurers, including without limitation public liability insurance at a minimum coverage level of AU$20,000,000." },
    { description: "You must provide a copy of your Insurance Documents 10 days prior to the Access Date. If you make a Booking with less than 10 days to go before the Access Date, you must provide a copy of your Insurance Documents immediately." },
  ]
};

export const BOOKING_TERMS_RISK_DOCUMENTS = {
  id: "risk-documents-aus",
  title: "Risk documents",
  options: [
    { description: "You must provide a copy of your Risk Documents 10 days prior to the Access Date. If you make a Booking with less than 10 days to go before the Access Date, you must provide a copy of your Risk Documents immediately." },
  ]
};

export const BOOKING_TERMS_ADDITIONAL_DOCUMENTATION = {
  id: "additional-documentation-aus",
  title: "Additional documentation",
  options: [
    { description: "You must complete or provide any Additional Documentation required by the Location Provider 10 days prior to the Access Date. If you make a Booking with less than 10 days to go before the Access Date, you must complete or provide any Additional Documentation immediately." },
  ]
};

export const BOOKING_TERMS_REQUIRED_APPROVALS = {
  id: "required-approvals-aus",
  title: "Required approvals",
  options: [
    { description: "You must request the Required Approvals (if any) from the Location Provider 10 days prior to the Access Date. If you make a Booking with less than 10 days to go before the Access Date, you must request the Required Approvals (if any) from the Location Provider immediately.", },
    { description: "The Booking Agent shall ensure that the Location Provider does not unreasonably withhold or delay the Required Approvals (if any)." },
    { description: "Any approval given by the Location Provider does not waive or release you from any liability under these Booking Terms or any applicable law." },
  ],
};

export const BOOKING_TERMS_VALUE_ADDED_TAX = {
  id: "value-added-tax-aus",
  title: "Goods and services tax (GST)",
  options: [
    { description: "All costs and charges are stated on a GST exclusive basis." },
    { description: "Unless otherwise stated in the Booking Confirmation, GST applies to the Cost of Space and any additional costs or charges." },
    { description: "No GST applies to the payment of a Security Deposit (if any) but may apply to amounts deducted from the Security Deposit (if any)." },
  ],
};

export const BOOKING_TERMS_BOOKING_CONFIRMATION = {
  id: "booking-confirmation-aus",
  title: "Booking Confirmation",
  options: [
    { description: "Your Booking is not final unless and until the Platform issues a Booking Confirmation." },
    { description: "The Booking Confirmation will include the details of your Booking, a copy of these Booking Terms and any Specifics of Hire. In the event of any conflict between any Specifics of Hire and these Booking Terms, the Specifics of Hire shall prevail." },
    { description: "If you have requested any additional requirements relating to your use of the Space and the Location Provider agrees to your request, the additional requirements will be included as Specifics of Hire in the Booking Confirmation. The Location Provider does not agree to any additional requirements unless they are included as Specifics of Hire in the Booking Confirmation." },
    { description: "The Platform will issue a Booking Confirmation after the Location Provider has accepted your Booking." },
  ],
};

export const BOOKING_TERMS_CHANGING_YOUR_BOOKING = {
  id: "changing-your-booking-aus",
  title: "Changing your Booking",
  options: [
    { description: "You can request changes to your Booking before or after the Platform issues a Booking Confirmation." },
    { description: "Any changes are subject to approval by the Location Provider." },
    { description: "You remain liable for the Booking Fee whether or not the changes are approved by the Location Provider and whether you request the changes before or after the Platform issues a Booking Confirmation." },
    { description: "If you request a change that is not approved by the Location Provider and you do not continue with your Booking, you may be deemed (by the Booking Agent or the Location Provider) to have cancelled your Booking and your cancellation will be subject to any applicable cancellation terms." },
  ],
};

export const BOOKING_TERMS_LICENCE_TO_OCCUPY = {
  id: "licence-to-occupy-aus",
  title: "Licence",
  options: [
    { description: "The Licence connected to your Booking will permit you to access, occupy and use the relevant Space in accordance with the permitted use and subject to any Specifics of Hire and any conduct requirements applicable to you, your client or any persons permitted on-site. The Location Provider may terminate your Licence with immediate effect without refund if you are in material breach of the Licence or you, your client or any persons permitted on-site are in material breach of any applicable conduct requirements." },
    { description: "You acknowledge and agree that no relationship of landlord and tenant will be created between you and the Location Provider under the Licence connected to your Booking or as a result of your or your client's occupation of the Space." },
    { description: "You acknowledge and agree that the Location Provider will retain control, possession and management of the Space and neither you nor your client has any right to exclude the Location Provider from the Space." },
    { description: "You acknowledge and agree that the Location Provider will retain control, possession and management of the Location within which the Space is located and neither you nor your client has any right to interfere in the Location Provider's use of the Location or in any other party's use of any other Space within the Location." },
    { description: "You acknowledge and agree that at the end of the hire period, you will no longer be permitted to access, occupy and use the Space and that if you continue to access, occupy or use the Space you may be liable to pay an overrun fee (which might be an hourly or daily rate and which might be more than the hourly or daily rates quoted as the original Cost of Space)." },
    { description: "You acknowledge and agree that you cannot assign the Licence." },
    { description: "You acknowledge and agree that, unless otherwise stated in the Booking Confirmation, the governing law of the Licence will be the law of the jurisdiction in which the Space is located." },
    { description: "In the event of any conflict between the Licence and these Booking Terms, the Licence shall prevail." },
  ],
};

export const BOOKING_TERMS_INSTALLATION = {
  id: "installation-and-de-installation-aus",
  title: "Installation and de-installation",
  options: [
    { description: "The Location Provider must approve all display materials and you must comply with any instructions given by the Location Provider relating to your display material." },
    { description: "The Location Provider must approve use of any sound, media, lighting or public-address systems and you must comply with any instructions given by the Location Provider relating to your use of those systems." },
    { description: "The Location Provider must approve any and all modifications or alterations to the interior or exterior of the Space prior to you making the modifications or alterations." },
    { description: "You are required to reinstate the Space to the condition the Location Provider provided it to you. The Location Provider may require an inventory check and site inspection prior to your installation and subsequent to your de-installation." },
    { description: "You are liable for any dilapidations and you must make good all dilapidations at your own cost." },
    { description: "Unless otherwise agreed, you are required to clean the Space and remove all rubbish from the Space at the end of your use of the Space. You are liable for all reasonable cleaning costs or the costs to remove rubbish." },
  ],
};

export const BOOKING_TERMS_CONSENTS = {
  id: "consents-aus",
  title: "Consents, licences and broadcast rights",
  options: [
    { description: "You are responsible for obtaining any consents or licences required in connection with your intended use of the Space including without limitation event licences, alcohol licences or planning licences." },
    { description: "You are responsible for ensuring you have the right to play, broadcast or display any pre-recorded or live music or other creative works and for paying for those rights." }
  ],
};

export const BOOKING_TERMS_SECURITY = {
  id: "security-aus",
  title: "Security",
  options: [
    { description: "Unless otherwise agreed, you must provide your own security for your event or related activity. However, you must comply (and ensure that security personnel comply) with any applicable security requirements and any instructions given by the Location Provider relating to security." }
  ],
};

export const BOOKING_TERMS_CANCELLATION = {
  id: "cancellation-aus",
  title: "Cancellation",
  options: [
    { description: "If you are unable to proceed with your Booking, it is your responsibility to cancel your Booking. You must not assume in any circumstances that your Booking has been automatically cancelled, including if you fail to make payment by a payment date or if you fail to deliver a document or fulfil any requirement by a specified date." },
    { description: "Unless otherwise stated in the Booking Confirmation, if you cancel your Booking within 45 days of the Access Date, you remain liable to pay 100% of the Booking Fee (including if you make your Booking with less than 45 days to go before the Access Date)." },
    { description: "The Booking Agent may cancel your Booking if you fail to make any payment by a payment date. Unless otherwise stated in the Booking Confirmation, if the Booking Agent cancels your Booking because you failed to make a payment by a payment date, you remain liable to pay 100% of the Booking Fee." },
    { description: "The Location Provider may cancel your Booking in circumstances beyond the Location Provider’s reasonable control if the Location Provider reasonably determines that those circumstance require the abandonment, postponement, interruption or cancellation of your promotional or other activity. If the Location Provider cancels on this basis then the Booking Agent will use all reasonable endeavours to ensure that the Location Provider refunds your Booking Fee. " },
  ],
};

export const BOOKING_TERMS_LIMITATION_OF_LIABILITY = {
  id: "limitation-of-liability-aus",
  title: "Limitation of liability",
  options: [
    { description: "Neither the Booking Agent nor the Location Provider gives any warranty that the Space or Location is physically fit or has any necessary consents required by any competent authorities for the purposes of your or your client’s intended use of the Space (including where those purposes fall within any specified permitted use of the Space). You acknowledge and agree that you do not rely on and you shall have no remedies in respect of any representation or warranty that might have been made by or on behalf of any party at any point in time in relation to the same subject matter." },
    { description: "Subject to the other paragraphs of this section, the Booking Agent owes no liability to your client and will only be liable to you for any reasonable losses, costs and expenses incurred by you in connection with your Booking which arise directly from: the Booking Agent’s breach of these Booking Terms; the Booking Agent’s negligence; or the Booking Agent’s breach of any statutory duty." },
    { description: "The Booking Agent’s liability to you in connection with your Booking is limited to any amounts paid by you to the Booking Agent for your Booking." },
    { description: "The Booking Agent will not be liable to you for any indirect or consequential loss including any loss of business, capital, profit, reputation or goodwill." },
    { description: "You acknowledge and agree that the Location Provider’s liability to you, your client or any persons permitted on-site is limited in accordance with the terms of the Licence connected to your Booking." },
    { description: "Nothing in these Booking Terms excludes any party’s liability for fraud." },
    { description: "Nothing in these Booking Terms excludes any party’s liability for death or personal injury or damage to property caused by that party (or the negligence of its employees or agents)." },
  ],
};

export const BOOKING_TERMS_INDEMNITY = {
  id: "indemnity-aus",
  title: "Indemnity",
  options: [
    { description: "You indemnify the Booking Agent and the Location Provider for any loss, cost or claim in connection with your Booking arising from: your breach of these Booking Terms; your breach of the terms of the Licence connected to your Booking; your or your client’s negligence; and your or your client’s breach of any statutory duty." },
    { description: "You acknowledge and agree that the Location Provider may enforce this indemnity directly against you in addition to any indemnity contained in the Licence connected to your Booking." }
  ],
};

export const BOOKING_TERMS_CHANGES = {
  id: "changes-booking-terms-aus",
  title: "Changes to these Booking Terms",
  options: [
    {
      description: <p>Location Live may change these Booking Terms at any time by updating the terms set out {"at "}
        <b><u><Link href="/bookingterms">https://locationlive.com/bookingterms</Link></u></b>.</p>
    },
    { description: "The changes made by an update only apply to Bookings made after the date of the update." },
  ],
};

export const THIRD_PARTY_RIGHTS = {
  id: "third-party-rights-aus",
  title: "Third party rights",
  options: [
    { description: "You acknowledge and agree that these Booking Terms apply to your Booking for the benefit of the Booking Agent and the Location Provider and that the Booking Agent and/or the Location Provider may enforce any term of these Booking Terms directly against you." },
    { description: "These Booking Terms do not create any rights for any person other than you, the Booking Agent and the Location Provider and no person other than you, the Booking Agent or the Location Provider has any right to enforce any term of these Booking Terms." },
  ],
};

export const BOOKING_TERMS_GOVERNING = {
  id: "governing-law-and-jurisdiction-aus",
  title: "Governing law and jurisdiction",
  options: [
    { description: "These Booking Terms and any dispute or claim arising out of or in connection with your Booking (including non-contractual disputes or claims) shall be governed by and construed in accordance with the law of New South Wales." },
    { description: "You submit to the courts of New South Wales to settle any dispute or claim arising out of or in connection with your Booking (including non-contractual disputes or claims)." },
  ],
};

export const DEFAULT_BOOKING_TERMS = [
  BOOKING_TERMS_MAKING_BOOKING,
  BOOKING_TERMS_AUTHORISATION,
  BOOKING_TERMS_ACCESS_CONDITIONS,
  BOOKING_TERMS_BOOKING_FEE,
  BOOKING_TERMS_SECURITY_DEPOSIT,
  BOOKING_TERMS_INSURANCE,
  BOOKING_TERMS_RISK_DOCUMENTS,
  BOOKING_TERMS_ADDITIONAL_DOCUMENTATION,
  BOOKING_TERMS_REQUIRED_APPROVALS,
  BOOKING_TERMS_VALUE_ADDED_TAX,
  BOOKING_TERMS_BOOKING_CONFIRMATION,
  BOOKING_TERMS_CHANGING_YOUR_BOOKING,
  BOOKING_TERMS_LICENCE_TO_OCCUPY,
  BOOKING_TERMS_INSTALLATION,
  BOOKING_TERMS_CONSENTS,
  BOOKING_TERMS_SECURITY,
  BOOKING_TERMS_CANCELLATION,
  BOOKING_TERMS_LIMITATION_OF_LIABILITY,
  BOOKING_TERMS_INDEMNITY,
  BOOKING_TERMS_CHANGES,
  THIRD_PARTY_RIGHTS,
  BOOKING_TERMS_GOVERNING,
];
